import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, OnInit, Output} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {TermsHttpService} from '../../services/terms-http.service';
import {PublishedTermsResponse, TermsApprovalStatus} from '../../models/terms-version.model';
import {AgentModel} from '../../../../agents/angular/models/agent.model';
import {AgentsHttpService} from '../../../../agents/angular/services/agents-http.service';
import {
  TERMS_APPROVALS_COLUMN_WIDTHS,
  TERMS_APPROVALS_PAGE_SIZE,
  TERMS_APPROVALS_TABLE_UNIQUE_ID
} from '../../constants/terms-approvals.constant';

require('./terms-approvals-panel.scss');
@Component({
  standalone: false,
  selector: 'app-terms-approvals-panel',
  templateUrl: './terms-approvals-panel.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TermsApprovalsPanelComponent implements OnInit {

  @Output() onClose = new EventEmitter<void>();

  public readonly tableUniqueId = TERMS_APPROVALS_TABLE_UNIQUE_ID;
  public readonly defaultWidths = TERMS_APPROVALS_COLUMN_WIDTHS;
  public readonly approvalStatuses = TermsApprovalStatus;

  public summary: PublishedTermsResponse;
  public filter: TermsApprovalStatus = TermsApprovalStatus.Approved;
  // The selected list's agents loaded so far - grows a page at a time as the table scrolls.
  public agents: AgentModel[] = [];
  // How many agents the selected list holds - the max of the bottom progress bar.
  public listSize = 0;
  public isLoading = false;

  // Empty once the selected list has no more pages.
  private nextCursor = '';

  constructor(@Inject(TermsHttpService) private termsHttpService: TermsHttpService,
              @Inject(AgentsHttpService) private agentsHttpService: AgentsHttpService,
              @Inject('bringozToastService') private bringozToastService: any,
              @Inject(ChangeDetectorRef) private cdr: ChangeDetectorRef) {
  }

  // The summary only feeds the header counts and the progress bar - the list itself comes from
  // the agents API, so both load side by side.
  ngOnInit() {
    this.termsHttpService.getPublished().subscribe({
      next: (summary) => {
        this.summary = summary;
        this.listSize = this.sizeOf(this.filter);
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => this.showError(err)
    });
    this.loadAgents();
  }

  // Switches the list and queries its agents from the first page.
  onToggleFilter(filter: TermsApprovalStatus) {
    if (this.filter === filter) {
      return;
    }
    this.filter = filter;
    this.listSize = this.sizeOf(filter);
    this.agents = [];
    this.loadAgents();
  }

  onFetchNext() {
    if (this.nextCursor && !this.isLoading) {
      this.loadAgents(this.nextCursor);
    }
  }

  private loadAgents(cursor?: string) {
    this.isLoading = true;
    this.cdr.markForCheck();

    // termsApproved: whether the agent approved the currently published version.
    const termsApproved = this.filter === TermsApprovalStatus.Approved;
    const params = cursor ? {termsApproved, cursor} : {termsApproved, limit: TERMS_APPROVALS_PAGE_SIZE};
    this.agentsHttpService.getAgents(params).subscribe({
      next: (res) => {
        this.agents = cursor ? [...this.agents, ...res.list] : res.list;
        this.nextCursor = res.cursor;
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => this.showError(err)
    });
  }

  private sizeOf(filter: TermsApprovalStatus): number {
    if (!this.summary) {
      return 0;
    }
    return filter === TermsApprovalStatus.Approved
      ? this.summary.approvedCount
      : this.summary.totalCount - this.summary.approvedCount;
  }

  private showError(err: HttpErrorResponse) {
    this.isLoading = false;
    this.cdr.markForCheck();
    const moreInfo: string = err?.error?.more_info;
    this.bringozToastService.error({
      body: 'TERMS_APPROVALS_LOAD_ERROR',
      message: moreInfo,
      details: moreInfo ? undefined : 'SOMETHING_WENT_WRONG'
    });
  }

}
